/**
 * CAOS-1583 — the screen a person opens to approve a headless client's sign-in.
 *
 * ## WHERE THIS LIVES, WHICH THE TICKET ASKED TO BE DECIDED RATHER THAN ASSUMED
 *
 * CAOS-1583 names this explicitly: "approval is a browser surface, which the Engine renders
 * exactly two of today — deciding which of those it is, or that it is a third, is part of this
 * ticket". The answer is that it is NEITHER of the two, and it is not installed metadata either.
 * It is host chrome, and the reasoning is short:
 *
 *  • The two BAKED surfaces — sign-in and empty-workspace — are baked because of a single stated
 *    property: they "exist BEFORE any package is installed and so cannot arrive as installed
 *    metadata" (`kernel/src/runtime/surface-decision.ts`). Device approval does not have that
 *    property. It is reached by somebody already signed in, so metadata has already resolved. It is
 *    not a third baked surface, and adding it as one would widen a category by its exception rather
 *    than by its rule.
 *
 *  • It cannot be installed METADATA either, and this is the half that actually decides it. The
 *    flow is a platform capability: a person must be able to approve a device in ANY org, including
 *    one that has installed nothing, or removed the package that happened to carry the screen. A
 *    capability whose availability depends on a package is not a capability — and the failure would
 *    be a headless client printing a URL that leads nowhere, in exactly the orgs least equipped to
 *    diagnose it.
 *
 *  • So it is the third thing, and the third thing already exists with a precedent: the report-a-bug
 *    and get-help panels in `shell-frame.ts` are host chrome that no metadata can remove and that
 *    no baked surface covers. This screen is that class. It is dispatched from the host's own
 *    pathname switch, beside `/setup` and `/my-profile`, which is where a path the workspace owns
 *    is answered.
 *
 * ## WHAT THE SCREEN HAS TO GET RIGHT
 *
 * It is a consent screen, and a consent screen that nobody reads is worse than no consent screen —
 * it manufactures a record of a decision that was not made. So:
 *
 *  • The client's name is the loudest thing on the page, because "is this thing mine?" is the only
 *    question the person can actually answer. They cannot verify a code they were handed.
 *  • Approve is NOT the default-looking action. Both buttons are explicit, and nothing is
 *    pre-selected: a person landing here from a link they did not expect should find no path that
 *    approves by reflex.
 *  • It says what approving GRANTS, in the terms the credential actually has — that the client
 *    will act as them, and for how long — rather than "authorize device?".
 *  • The code is echoed from what the person typed or what the link carried, never read back from
 *    the server. The server stores only a hash and has nothing to echo, which is deliberate: there
 *    is no read path here that could confirm a guessed code to somebody enumerating.
 */
import { actions, brandline, button, card } from './dom.js';

/**
 * The path this screen answers on.
 *
 * The KERNEL owns this string — it builds it into the `verification_uri` a headless client prints
 * and a person reads aloud (`kernel/src/api/auth-routes.ts`, `DEVICE_VERIFICATION_PATH`). The host
 * has no dependency on the kernel package, so it is declared here as well, following the same
 * pattern the rest of this folder uses for its paths.
 *
 * Two declarations of one string across a package boundary is a drift risk and not a theoretical
 * one: the failure would be a person typing a URL that 404s while every test on both sides passes,
 * and the only person who sees it is the one who cannot debug it. So the host test reads the
 * kernel's declaration out of its source and pins the two together — the same way the component
 * library's host test reaches across to `caos-ui/src/catalog.js` rather than trusting a copy.
 */
export const DEVICE_VERIFICATION_PATH = '/device';

/**
 * How long the credential a completed grant mints lasts, in hours.
 *
 * On the page because it is the half of the consent that makes it a decision — "authorize device?"
 * asks somebody to agree to an unspecified thing — and pinned to the kernel's
 * `DEVICE_ACCESS_TOKEN_LIFETIME_MS` by the same test that pins the path, for the same reason: a
 * screen that promises eight hours over a credential that lasts a week is a consent obtained under
 * a false statement, and nothing else in either package would notice.
 */
export const DEVICE_ACCESS_TOKEN_HOURS = 8;

/** True iff this pathname is the device approval screen's. */
export function isDeviceApprovalPath(pathname: string): boolean {
  return pathname === DEVICE_VERIFICATION_PATH || pathname === `${DEVICE_VERIFICATION_PATH}/`;
}

/** What the kernel tells this screen about the grant awaiting an answer. */
export interface PendingGrant {
  /** The label the requesting client supplied. Untrusted text: it grants nothing. */
  readonly clientName: string;
  readonly requestedAt: string;
  readonly expiresAt: string;
}

/** Why there is nothing to approve. Each maps to one sentence a person can act on. */
export type ApprovalProblem = 'unknown' | 'expired' | 'already_answered';

export interface DeviceApprovalOptions {
  /** The code from the link's `?code=` or typed into the form. Echoed, never fetched. */
  readonly userCode: string;
  /** The grant, once looked up; absent while the code has not been resolved yet. */
  readonly grant?: PendingGrant | undefined;
  /** Set instead of `grant` when the code resolved to nothing usable. */
  readonly problem?: ApprovalProblem | undefined;
  /** How long the credential the client receives will last, in hours, for the consent copy. */
  readonly credentialHours: number;
  readonly onApprove?: (() => void) | undefined;
  readonly onDeny?: (() => void) | undefined;
  readonly onSubmitCode?: ((code: string) => void) | undefined;
}

/**
 * What each problem says.
 *
 * `unknown` deliberately covers a mistyped code, a code from another org and a code that was
 * already collected, and says the same thing for all three. Telling somebody which it was is a
 * confirmation oracle: "already collected" tells whoever replayed a leaked code that it was real.
 * The sentence is written to be useful anyway — every one of the three has the same remedy.
 */
const PROBLEM_COPY: Record<ApprovalProblem, string> = {
  unknown:
    'We could not find a sign-in request with that code. Check the code and try again, or start ' +
    'the sign-in again from the app that asked.',
  expired:
    'This request has expired. Start the sign-in again from the app that asked, and approve the ' +
    'new code it shows you.',
  already_answered: 'Somebody has already answered this request. Nothing more to do here.',
};

/** A paragraph of host-chrome body copy. */
function paragraph(text: string): HTMLParagraphElement {
  const p = document.createElement('p');
  p.className = 'host-sub';
  p.textContent = text;
  return p;
}

/** The code, shown big and monospaced so a person can compare it with the one on their screen. */
function codeEcho(userCode: string): HTMLElement {
  const el = document.createElement('div');
  el.className = 'host-device-code';
  // A `code` element rather than a styled div: a screen reader should read this character by
  // character, because the whole point is comparing it against a string on another screen.
  const code = document.createElement('code');
  code.textContent = userCode;
  el.append(code);
  return el;
}

/**
 * The form for a person who opened the plain verification URL with no code in it — the headless
 * case the RFC's `verification_uri` exists for, and the one that actually happens: a server with
 * no browser prints a URL and a code, and the person types both on a different machine.
 */
function codeForm(onSubmitCode: (code: string) => void): HTMLElement {
  const form = document.createElement('form');
  form.className = 'host-field';

  const label = document.createElement('label');
  label.className = 'host-field-label';
  label.htmlFor = 'device-user-code';
  label.textContent = 'Enter the code shown by the app';

  const input = document.createElement('input');
  input.className = 'host-input';
  input.id = 'device-user-code';
  input.type = 'text';
  // A code is read off one screen and typed into another, so nothing may transform it on the way
  // in: autocorrect and autocapitalise are exactly the features that break a transcribed code, and
  // `off` on all four is the only combination browsers honour consistently.
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('autocorrect', 'off');
  input.setAttribute('autocapitalize', 'characters');
  input.setAttribute('spellcheck', 'false');
  input.placeholder = 'XXXXX-XXXXX';

  const submit = button('Continue', 'primary');
  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    onSubmitCode(input.value);
  });
  submit.addEventListener('click', () => onSubmitCode(input.value));

  form.append(label, input, actions(submit));
  return form;
}

/**
 * Render the device approval screen.
 *
 * Returns the element rather than mounting it, like every other surface in this folder, so the
 * host's dispatcher owns where it goes.
 */
export function renderDeviceApprovalSurface(options: DeviceApprovalOptions): HTMLElement {
  const root = document.createElement('div');
  root.className = 'host-surface host-device-approval';

  const panel = card();
  panel.append(brandline('Approve a sign-in'));

  // Nothing to approve: say which of the three it is, and stop. No approve control is rendered at
  // all here rather than rendered-and-disabled — a disabled Approve button on a dead request is an
  // invitation to keep clicking.
  if (options.problem) {
    panel.append(paragraph(PROBLEM_COPY[options.problem]));
    root.append(panel);
    return root;
  }

  // No code yet: ask for one.
  if (!options.grant) {
    panel.append(
      paragraph(
        'An app asked to sign in as you. It showed you a short code — enter it here so we know ' +
          'which request to approve.',
      ),
    );
    if (options.onSubmitCode) panel.append(codeForm(options.onSubmitCode));
    root.append(panel);
    return root;
  }

  const grant = options.grant;

  // The client's name first and on its own line. It is the one thing the person can actually
  // judge, and burying it under the code would invert that.
  const who = document.createElement('h2');
  who.className = 'host-device-client';
  who.textContent = grant.clientName;
  panel.append(who);

  panel.append(
    paragraph(
      `${grant.clientName} is asking to sign in as you. If you did not start this, close this ` +
        'page and do not approve it.',
    ),
  );

  if (options.userCode) {
    panel.append(paragraph('Check this matches the code the app showed you:'));
    panel.append(codeEcho(options.userCode));
  }

  // What approving actually grants, in the terms the credential has. "Authorize device?" tells a
  // person nothing they can weigh; "it will act as you for eight hours, and can renew that" is a
  // thing somebody can decide about.
  panel.append(
    paragraph(
      `If you approve, it will be able to do anything you can do, for ${options.credentialHours} ` +
        'hours at a time, and to renew that without asking you again until you sign it out.',
    ),
  );

  const approve = button('Approve', 'primary');
  const deny = button('Do not approve', 'secondary');
  if (options.onApprove) approve.addEventListener('click', () => options.onApprove!());
  if (options.onDeny) deny.addEventListener('click', () => options.onDeny!());
  // Deny FIRST in the DOM, so the keyboard order reaches it before Approve. A consent screen whose
  // first focusable action is the irreversible one is a consent screen that collects reflexes.
  panel.append(actions(deny, approve));

  root.append(panel);
  return root;
}
